// In production (same origin), use empty string for relative URLs
// In development, use the configured API URL
const API_BASE_URL =
  import.meta.env.VITE_API_URL ?? (import.meta.env.PROD ? "" : "http://localhost:3001");

type ValidationError = {
  path: (string | number)[];
  message: string;
};

type ApiResponse<T> = {
  success: boolean;
  data?: T;
  error?: string;
  details?: ValidationError[];
};

// Format validation errors into a readable message
function formatValidationErrors(details: ValidationError[]): string {
  return details
    .map((err) => {
      const field = err.path.length > 0 ? `${err.path.join(".")}: ` : "";
      return `${field}${err.message}`;
    })
    .join(". ");
}

// Custom error class that preserves the HTTP status code from the API response.
// Use `err instanceof ApiError` and `err.status` to branch on specific codes (e.g. 413, 408).
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });

    const json: ApiResponse<T> = await response.json();

    if (!response.ok || !json.success) {
      // If we have validation details, format them into a readable message
      if (json.details && json.details.length > 0) {
        throw new ApiError(response.status, formatValidationErrors(json.details));
      }
      throw new ApiError(response.status, json.error || "An error occurred");
    }

    return json.data as T;
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint);
  }

  async post<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  async put<T>(endpoint: string, body: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
    });
  }

  async uploadFile<T>(
    endpoint: string,
    file: File,
    additionalData?: Record<string, string>
  ): Promise<T> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      for (const [key, value] of Object.entries(additionalData)) {
        formData.append(key, value);
      }
    }

    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      method: "POST",
      credentials: "include",
      body: formData,
    });

    const json: ApiResponse<T> = await response.json();

    if (!response.ok || !json.success) {
      // If we have validation details, format them into a readable message
      if (json.details && json.details.length > 0) {
        throw new Error(formatValidationErrors(json.details));
      }
      throw new Error(json.error || "An error occurred");
    }

    return json.data as T;
  }
}

export const api = new ApiClient(API_BASE_URL);

// Plugin types
export interface PluginFileMetadata {
  // Common fields
  name?: string;
  description?: string;
  version?: string;
  model?: string;

  // Skill/Command fields
  allowed_tools?: string[];
  argument_hints?: string;

  // Agent fields
  tools?: string[];
  permissionMode?: string;
  skills?: string[];
  color?: string;

  // Allow any additional fields
  [key: string]: unknown;
}

export interface PluginFile {
  id: string;
  fileName: string;
  fileType: "SKILL" | "COMMAND" | "AGENT" | "HOOK" | "MCP_CONFIG" | "OTHER";
  s3Url: string;
  size: number;
  displayName: string | null;
  description: string | null;
  metadata: PluginFileMetadata | null;
}

export interface Plugin {
  id: string;
  name: string;
  slug: string;
  description: string;
  shortDesc: string | null;
  authorName: string | null;
  authorEmail: string | null;
  repositoryUrl: string | null;
  iconUrl: string | null;
  s3BaseUrl: string | null;
  tags: string[];
  status: "DRAFT" | "PENDING_REVIEW" | "APPROVED" | "REJECTED" | "PUBLISHED";
  featured: boolean;
  published: boolean;
  reviewNote: string | null;
  pricingModel: "FREE" | "ONE_TIME" | "SUBSCRIPTION" | "FREEMIUM";
  price: number | null;
  authorId: string;
  author: {
    id: string;
    name: string;
    avatarUrl: string | null;
  };
  files: PluginFile[];
  versions: {
    id: string;
    version: string;
    changelog: string | null;
    publishedAt: string;
  }[];
  // Fork relationships
  forkedFromId: string | null;
  forkedFrom: {
    id: string;
    name: string;
    slug: string;
    authorName: string | null;
    author: { name: string };
  } | null;
  _count: {
    downloads: number;
    reviews: number;
    favorites: number;
    forks: number;
  };
  createdAt: string;
  updatedAt: string;
  submittedAt: string | null;
  approvedAt: string | null;
  lastSyncedAt?: string | null;
  upstreamVersion?: string | null;
}

// Plugin API functions
export const pluginApi = {
  getPopularTags: () => api.get<string[]>("/plugins/tags"),
  getPublishedPlugins: () => api.get<Plugin[]>("/api/plugins"),
  getMyPlugins: () => api.get<Plugin[]>("/plugins/my-plugins"),
  getPlugin: (slug: string) => api.get<Plugin>(`/plugins/${slug}`),
  createPlugin: (data: {
    name: string;
    description: string;
    shortDesc?: string;
    tags?: string[];
    version: string;
    repositoryUrl?: string;
  }) => api.post<Plugin>("/plugins", data),
  uploadFile: (
    pluginId: string,
    file: File,
    fileType: PluginFile["fileType"],
    relativePath?: string
  ) =>
    api.uploadFile<{
      id: string;
      fileName: string;
      s3Url: string;
      displayName: string | null;
      description: string | null;
      metadata: PluginFileMetadata | null;
    }>(`/plugins/${pluginId}/files`, file, { fileType, ...(relativePath && { relativePath }) }),
  deleteFile: (pluginId: string, fileId: string) =>
    api.delete<{ message: string }>(`/plugins/${pluginId}/files/${fileId}`),
  deletePlugin: (pluginId: string) => api.delete<{ message: string }>(`/plugins/${pluginId}`),
  submitForReview: (pluginId: string) => api.post<Plugin>(`/plugins/${pluginId}/submit`, {}),
  forkPlugin: (pluginId: string) => api.post<Plugin>(`/plugins/${pluginId}/fork`, {}),
  // Favorites
  getUserFavorites: () => api.get<string[]>("/plugins/favorites/list"),
  toggleFavorite: (pluginId: string) =>
    api.post<{ favorited: boolean; count: number }>(`/plugins/${pluginId}/favorite`, {}),
  getFavoriteStatus: (pluginId: string) =>
    api.get<{ favorited: boolean; count: number }>(`/plugins/${pluginId}/favorite`),
  // GitHub import
  fetchGithubRepo: (url: string) =>
    api.post<{
      files: { path: string; content: string }[];
      pluginJson?: { name?: string; description?: string; version?: string; keywords?: string[] };
      pluginRoot: string;
    }>("/plugins/github/fetch", { url }),
  // New this week
  getNewThisWeek: () => api.get<Plugin[]>("/api/plugins/new-this-week"),
};

// Stats types
export interface PlatformStats {
  pluginCount: number;
  downloadCount: number;
  marketplaceCount: number;
}

// Stats API
export const statsApi = {
  getStats: () => api.get<PlatformStats>("/api/stats"),
};

// Pack types
export interface Pack {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string | null;
  color: string | null;
  tagFilter: string;
  featured: boolean;
  order: number;
  pluginCount: number;
}

export interface PackWithPlugins extends Omit<Pack, "pluginCount"> {
  plugins: Plugin[];
}

// Pack API functions
export const packApi = {
  getAllPacks: () => api.get<Pack[]>("/api/packs"),
  getPackBySlug: (slug: string) => api.get<PackWithPlugins>(`/api/packs/${slug}`),
};

// Skill Creator types
export interface SkillCreatorMessage {
  role: "user" | "assistant";
  content: string;
}

export interface SkillFile {
  filename: string;
  content: string;
}

export interface SkillCreatorResponse {
  response: string;
  skillFiles?: SkillFile[];
}

// Skill Creator API functions
export const skillCreatorApi = {
  chat: (messages: SkillCreatorMessage[]) =>
    api.post<SkillCreatorResponse>("/api/skill-creator/chat", { messages }),
};

// Admin API functions
export const adminApi = {
  getPendingPlugins: () => api.get<Plugin[]>("/admin/plugins/pending"),
  getPlugin: (id: string) => api.get<Plugin>(`/admin/plugins/${id}`),
  reviewPlugin: (pluginId: string, status: "APPROVED" | "REJECTED", reviewNote?: string) =>
    api.post<Plugin>(`/admin/plugins/${pluginId}/review`, { status, reviewNote }),
};

// Marketplace types
export interface Marketplace {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  _count: {
    plugins: number;
  };
}

export interface MarketplaceDetail {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  ownerId: string;
  createdAt: string;
  updatedAt: string;
  plugins: Array<{
    pluginId: string;
    order: number;
  }>;
}

export interface MarketplaceWithPlugins {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  owner: {
    id: string;
    name: string;
    avatarUrl: string | null;
  };
  createdAt: string;
  updatedAt: string;
  plugins: Array<
    Plugin & {
      order: number;
      category: string | null;
      addedAt: string;
    }
  >;
}

export interface PublicMarketplaceSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  owner: {
    id: string;
    name: string;
    avatarUrl: string | null;
  };
  pluginCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface MarketplaceSummary {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon?: string | null;
  pluginCount: number;
}

// Marketplace API functions
export const marketplaceApi = {
  getAllPublicMarketplaces: () => api.get<PublicMarketplaceSummary[]>("/api/marketplaces"),
  getFeatured: () => api.get<MarketplaceSummary[]>("/api/marketplaces/featured"),
  getUserMarketplaces: () => api.get<Marketplace[]>("/marketplaces"),
  getMarketplace: (id: string) => api.get<MarketplaceDetail>(`/marketplaces/${id}`),
  createMarketplace: (data: { name: string; description?: string }) =>
    api.post<Marketplace>("/marketplaces", data),
  updateMarketplace: (id: string, data: { name?: string; description?: string | null }) =>
    api.put<Marketplace>(`/marketplaces/${id}`, data),
  deleteMarketplace: (id: string) => api.delete<{ deleted: boolean }>(`/marketplaces/${id}`),
  addPlugin: (marketplaceId: string, pluginId: string, order?: number) =>
    api.post<{ id: string }>(`/marketplaces/${marketplaceId}/plugins`, { pluginId, order }),
  removePlugin: (marketplaceId: string, pluginId: string) =>
    api.delete<{ removed: boolean }>(`/marketplaces/${marketplaceId}/plugins/${pluginId}`),
  reorderPlugins: (marketplaceId: string, plugins: Array<{ pluginId: string; order: number }>) =>
    api.put<{ reordered: boolean }>(`/marketplaces/${marketplaceId}/plugins/reorder`, { plugins }),
  getPublicMarketplace: (slug: string) =>
    api.get<MarketplaceWithPlugins>(`/api/marketplaces/${slug}`),
};

// Stripe / Payment types
export interface ConnectStatus {
  connected: boolean;
  chargesEnabled: boolean;
  onboardingComplete: boolean;
  accountId?: string;
}

export interface Purchase {
  id: string;
  pluginId: string;
  amount: number;
  platformFee: number;
  status: "PENDING" | "COMPLETED" | "REFUNDED";
  createdAt: string;
  plugin: {
    id: string;
    name: string;
    slug: string;
    description: string;
    shortDesc: string | null;
    authorName: string | null;
    iconUrl: string | null;
    tags: string[];
  };
}

export interface CreatorEarnings {
  totalEarnings: number;
  totalSales: number;
  recentSales: Array<{
    pluginName: string;
    amount: number;
    platformFee: number;
    creatorEarnings: number;
    date: string;
  }>;
  perPlugin: Array<{
    pluginId: string;
    name: string;
    slug: string;
    sales: number;
    earnings: number;
  }>;
}

// Stripe API functions
export const stripeApi = {
  // Creator onboarding
  connectOnboard: () =>
    api.post<{ url: string | null; accountId: string; alreadyComplete?: boolean }>(
      "/api/stripe/connect/onboard",
      {}
    ),
  connectStatus: () => api.get<ConnectStatus>("/api/stripe/connect/status"),
  dashboardLink: () => api.get<{ url: string }>("/api/stripe/connect/dashboard-link"),

  // Plugin pricing
  setPluginPricing: (pluginId: string, pricingModel: string, price: number | null) =>
    api.put<Plugin>(`/api/stripe/plugins/${pluginId}/pricing`, { pricingModel, price }),

  // Checkout
  createCheckout: (pluginId: string) =>
    api.post<{ url: string }>("/api/stripe/checkout", { pluginId }),

  // Purchases
  getPurchases: () => api.get<Purchase[]>("/api/stripe/purchases"),
  getPurchaseStatus: (pluginId: string) =>
    api.get<{ purchased: boolean }>(`/api/stripe/purchases/${pluginId}/status`),

  // Earnings
  getEarnings: () => api.get<CreatorEarnings>("/api/stripe/earnings"),
};
